import React, { useState } from 'react';
import { Check, CircleAlert, ShieldCheck } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import { Reveal } from '../components/Alt4Shared';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  practiceName: '',
  providers: '',
  role: '',
};

const walkthroughTopics = [
  'How work moves across the patient journey',
  'How autopilot operates within your rules and permissions',
  'How leaders and local teams share one operating view',
  'How migration can follow location-level readiness',
];

export default function Alt4Walkthrough() {
  const [formData, setFormData] = useState(initialForm);
  const [submitState, setSubmitState] = useState('idle');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitState('submitting');

    try {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8011' : '');
      const response = await fetch(`${apiUrl}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Request failed');
      setSubmitState('success');
    } catch (error) {
      setSubmitState('error');
    }
  };

  return (
    <Alt4Layout title="Request a walkthrough">
      <section className="alt4-page-hero alt4-section">
        <div className="alt4-container alt4-walkthrough-grid">
          <Reveal className="alt4-walkthrough-copy">
            <p className="alt4-eyebrow">A guided operating walkthrough</p>
            <h1>See an AI-native PMS run the work.</h1>
            <p>
              Explore how Asha can observe your live practice state, carry out authorized tasks, verify outcomes, and bring the right exceptions to your team.
            </p>
            <div className="alt4-panel alt4-walkthrough-agenda">
              <span className="alt4-interface-label">Built around your operating model</span>
              <ul className="alt4-check-list">
                {walkthroughTopics.map((topic) => (
                  <li key={topic}><Check aria-hidden="true" /><span>{topic}</span></li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="alt4-panel alt4-walkthrough-form" delay={0.08}>
            {submitState === 'success' ? (
              <div className="alt4-form-status alt4-form-status--success" role="status">
                <Check aria-hidden="true" />
                <div>
                  <h2>Your request is in.</h2>
                  <p>Our team will follow up to shape the walkthrough around your organization.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="alt4-walkthrough-form__intro">
                  <p className="alt4-eyebrow">Request a walkthrough</p>
                  <h2>Tell us about your practice group.</h2>
                  <p>We’ll focus the conversation on your locations, workflows, controls, and transition needs.</p>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="alt4-form-grid">
                    <label className="alt4-field">
                      <span>First name</span>
                      <input name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field">
                      <span>Last name</span>
                      <input name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field">
                      <span>Work email</span>
                      <input name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field">
                      <span>Phone</span>
                      <input name="phone" type="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field">
                      <span>Practice or group</span>
                      <input name="practiceName" autoComplete="organization" value={formData.practiceName} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field">
                      <span>Number of providers</span>
                      <input name="providers" type="number" min="1" inputMode="numeric" value={formData.providers} onChange={handleChange} required />
                    </label>
                    <label className="alt4-field alt4-field--wide">
                      <span>Your role</span>
                      <select name="role" value={formData.role} onChange={handleChange} required>
                        <option value="" disabled>Select your role</option>
                        <option value="Owner / dentist">Owner / dentist</option>
                        <option value="Executive leader">Executive leader</option>
                        <option value="Operations leader">Operations leader</option>
                        <option value="Practice leader">Practice leader</option>
                        <option value="Technology leader">Technology leader</option>
                        <option value="Other">Other</option>
                      </select>
                    </label>
                  </div>

                  {submitState === 'error' && (
                    <div className="alt4-form-status alt4-form-status--error" role="alert">
                      <CircleAlert aria-hidden="true" />
                      <span>We couldn’t send your request. Please try again.</span>
                    </div>
                  )}

                  <button className="alt4-button" type="submit" disabled={submitState === 'submitting'}>
                    {submitState === 'submitting' ? 'Sending request…' : 'Request a walkthrough'}
                  </button>
                  <p className="alt4-form-note"><ShieldCheck aria-hidden="true" /> Your information is used only to follow up about your request.</p>
                </form>
              </>
            )}
          </Reveal>
        </div>
      </section>
    </Alt4Layout>
  );
}
